import type { AudioAsset } from '../../types';
import { fetchMedia } from './pack';

// Web Audio playback, precomputed-peak waveforms, and decoded samples.
//
// Rendering the waveform needs only the peaks array (no AudioContext), so the
// UI can draw before any user gesture. The AudioContext is created lazily on the
// first play() — this avoids the browser's "AudioContext not allowed to start"
// warning and keeps initial render cheap.
//
// The spectrogram strip needs more than an envelope: it needs the samples
// themselves, before the first tap. getSamples() decodes through an
// OfflineAudioContext when no playback context exists yet, which has no autoplay
// policy attached to it, and hands the same buffer to playback afterwards.
//
// When the real recording can't be fetched (pre-pipeline skeleton, or offline
// before the pack is downloaded) we synthesize a short, species-distinct
// placeholder tone so the Sound ID flow is still exercisable. Synthetic clips
// are flagged so the UI can label them.

let ctx: AudioContext | null = null;
function audioCtx(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

/** Opus decodes at 48 kHz, so decoding at that rate resamples nothing. */
const DECODE_RATE = 48_000;

let offline: OfflineAudioContext | null = null;

/**
 * A context to decode and allocate through, which is not always the playback
 * one.
 *
 * The spectrogram strip needs a clip's samples BEFORE anything has been tapped,
 * and creating the playback AudioContext to get them would spend it: the
 * browser logs "AudioContext not allowed to start", and on iOS the context that
 * matters is then born outside a gesture. An OfflineAudioContext carries no
 * autoplay policy — it exists to render, not to sound — and decodes the same
 * bytes to the same PCM. AudioBuffers are not bound to the context that made
 * them (see ensureRunning), so playback picks the result straight up, and a
 * source node resamples if the device runs at 44.1 kHz.
 */
function decodeCtx(): BaseAudioContext {
  if (ctx) return ctx;
  if (typeof OfflineAudioContext === 'undefined') return audioCtx();
  if (!offline) offline = new OfflineAudioContext(1, 1, DECODE_RATE);
  return offline;
}

/**
 * The context, actually running.
 *
 * iOS parks an AudioContext in `interrupted` — a state no other engine has, and
 * one the DOM's own AudioContextState type does not name — when the app
 * backgrounds, the screen locks, or another app takes over the audio session. A
 * context in that state accepts start() without complaint and plays nothing,
 * and its currentTime stops advancing, so the caller gets neither sound NOR
 * progress. Testing only for `suspended`, as this did, walks straight past it.
 *
 * resume() revives it in the ordinary case. When it does not, the context is
 * gone for good and only a new one will make sound, so we build one. Decoded
 * buffers survive that swap: an AudioBuffer is plain PCM rather than something
 * bound to the context that decoded it, and a source node resamples if the
 * replacement runs at a different rate — a real possibility when the
 * interruption was a pair of headphones connecting.
 */
async function ensureRunning(): Promise<AudioContext> {
  let ac = audioCtx();
  // Synchronously, while the tap that got us here is still live: a closed
  // context can only ever be replaced, and a fresh context's resume() counts
  // for much more inside the gesture than after an await.
  if (ac.state === 'closed') {
    ctx = null;
    ac = audioCtx();
  }
  if (ac.state !== 'running') {
    try {
      await ac.resume();
    } catch {
      /* unrevivable — replaced below */
    }
  }
  if (ac.state !== 'running') {
    try {
      await ac.close();
    } catch {
      /* already gone */
    }
    ctx = null;
    ac = audioCtx();
    try {
      await ac.resume();
    } catch {
      /* reported below */
    }
  }
  if (ac.state !== 'running') throw new Error(`audio context is ${ac.state}`);
  return ac;
}

// Coming back to the app is the one moment the context can be revived without
// spending a tap on it, so the first strip the learner reaches for plays at
// once. Best-effort: iOS may refuse outside a gesture, and play() resumes again
// anyway.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const ac = ctx;
    if (!ac || document.visibilityState !== 'visible') return;
    if (ac.state === 'running' || ac.state === 'closed') return;
    ac.resume().catch(() => {
      /* refused — the next tap tries again */
    });
  });
}

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

// ---- Peaks (no AudioContext) ----------------------------------------------

export interface Peaks {
  peaks: number[];
  synthetic: boolean;
}

const peaksCache = new Map<string, Promise<Peaks>>();

export function getPeaks(asset: AudioAsset): Promise<Peaks> {
  const hit = peaksCache.get(asset.id);
  if (hit) return hit;
  const p = getPeaksUncached(asset);
  peaksCache.set(asset.id, p);
  return p;
}

async function getPeaksUncached(asset: AudioAsset): Promise<Peaks> {
  // Peaks ride inline in the manifest (base64 of one byte per sample), so this
  // needs no fetch and works before the media pack is downloaded.
  if (asset.peaks) {
    try {
      const bin = atob(asset.peaks);
      const peaks = Array.from(bin, (c) => +(c.charCodeAt(0) / 255).toFixed(3));
      if (peaks.length) return { peaks, synthetic: false };
    } catch {
      /* malformed — fall through to the synthetic shape */
    }
  }
  // No precomputed peaks available (skeleton / missing) → synthesize a shape.
  return { peaks: synthPeaks(asset), synthetic: !asset.file };
}

/** Deterministic waveform shape for a placeholder tone. */
function synthPeaks(asset: AudioAsset, n = 48): number[] {
  const h = hashId(asset.id);
  const peaks: number[] = [];
  const bursts = 2 + (h % 3);
  for (let i = 0; i < n; i++) {
    const t = i / n;
    let v = 0;
    for (let b = 0; b < bursts; b++) {
      const center = (b + 0.5) / bursts;
      const width = 0.06 + ((h >> (b * 3)) & 7) / 60;
      v += Math.exp(-((t - center) ** 2) / (2 * width * width));
    }
    const jitter = 0.85 + 0.3 * (((h >> i) & 3) / 3);
    peaks.push(Math.min(1, v * jitter));
  }
  const max = Math.max(...peaks, 0.001);
  return peaks.map((p) => +(p / max).toFixed(3));
}

// ---- Playback (lazy AudioContext) -----------------------------------------

/** A decoded clip, and whether it is the recording or the stand-in. */
interface Decoded {
  buffer: AudioBuffer;
  /** True when getBufferUncached fell through to a generated tone. */
  synthetic: boolean;
}

const bufferCache = new Map<string, Promise<Decoded>>();

/**
 * How many decoded clips to hold. Mono float samples at 48 kHz are ~192 KB per
 * second, so a typical 7 s clip is ~1.3 MB and this is a ceiling around 16 MB.
 *
 * It used to be unbounded, which was fine while a buffer only ever arrived by
 * being played: you cannot tap fast enough to matter. The spectrogram strip
 * decodes what you merely SCROLL PAST, so a long session down the library would
 * have accumulated every clip in the pack. Eviction is by insertion order and
 * costs a re-decode at worst; a buffer that is currently sounding is held alive
 * by its source node either way.
 */
const BUFFER_CACHE_MAX = 12;

function getBuffer(asset: AudioAsset): Promise<Decoded> {
  const hit = bufferCache.get(asset.id);
  if (hit) return hit;
  const p = getBufferUncached(asset);
  bufferCache.set(asset.id, p);
  for (const id of bufferCache.keys()) {
    if (bufferCache.size <= BUFFER_CACHE_MAX) break;
    bufferCache.delete(id);
  }
  return p;
}

/** Decoded mono samples, or null if this clip could not be decoded at all. */
export interface Samples {
  data: Float32Array;
  rate: number;
  /** A generated tone rather than the recording — see getBufferUncached. */
  synthetic: boolean;
}

/**
 * The clip's samples, decoding it if that has not happened yet.
 *
 * Channel 0 only. Mixing a stereo recording down would copy the whole clip to
 * change a picture almost imperceptibly — xeno-canto field recordings are
 * overwhelmingly mono, and where they are not, one channel is the same bird.
 */
export async function getSamples(asset: AudioAsset): Promise<Samples | null> {
  try {
    const { buffer, synthetic } = await getBuffer(asset);
    return { data: buffer.getChannelData(0), rate: buffer.sampleRate, synthetic };
  } catch {
    // No decoder, no context, nothing fetched — the caller draws what it can.
    return null;
  }
}

/**
 * Resample an amplitude envelope down to `n` buckets, by taking the loudest
 * sample in each. Never upsamples: asked for more buckets than it has peaks it
 * returns what it has, and the strip draws them as wider bars.
 *
 * That refusal is the design. A clip ships 48 measured peaks in the manifest,
 * and a phone-width strip has room for ~114 bars; every way of filling the gap
 * is worse than leaving it. Interpolating draws a shape nothing measured.
 * Repeating each value across the bars it spans looks like detail from a
 * distance but is 48 values wearing 114 bars, and the moment anything replaced
 * it with truly 114-valued data the strip gained visible vertical detail under
 * the reader — which is exactly what it used to do on the first tap. So the
 * strip draws the peaks that exist, at the width they deserve, and never
 * changes again.
 */
export function resample(peaks: number[], n: number): number[] {
  if (peaks.length <= n || n < 1) return peaks;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const lo = Math.floor((i * peaks.length) / n);
    const hi = Math.max(lo + 1, Math.floor(((i + 1) * peaks.length) / n));
    let m = 0;
    for (let j = lo; j < hi; j++) if (peaks[j] > m) m = peaks[j];
    out.push(m);
  }
  return out;
}

async function getBufferUncached(asset: AudioAsset): Promise<Decoded> {
  if (asset.file) {
    const res = await fetchMedia(asset.file);
    if (res) {
      try {
        const buffer = await decodeCtx().decodeAudioData(await res.arrayBuffer());
        return { buffer, synthetic: false };
      } catch {
        /* fall through to synthetic */
      }
    }
  }
  return { buffer: synthBuffer(asset), synthetic: true };
}

/** A species-distinct warbling chirp, generated on demand. */
function synthBuffer(asset: AudioAsset): AudioBuffer {
  const ac = decodeCtx();
  const seconds = Math.min(Math.max(asset.dur || 3, 2), 4);
  const rate = ac.sampleRate;
  const len = Math.floor(seconds * rate);
  const buffer = ac.createBuffer(1, len, rate);
  const ch = buffer.getChannelData(0);
  const base = 900 + (hashId(asset.id) % 2200);
  for (let i = 0; i < len; i++) {
    const t = i / rate;
    const trill = Math.sin(2 * Math.PI * 6 * t);
    const freq = base + 220 * trill;
    const env = Math.pow(Math.sin((Math.PI * (t % 0.5)) / 0.5), 2) * Math.exp(-t * 0.4);
    ch[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.35;
  }
  return buffer;
}

/**
 * How a clip is to be played. Both knobs are also movable mid-clip, through the
 * Playback handle below — changing either restarts nothing.
 */
export interface PlayOptions {
  /**
   * Speed, as a multiple of the recording's own.
   *
   * A resampled buffer source is a slowed tape rather than a time-stretch, so
   * half speed drops the pitch an octave with it. That is the trade, and it is
   * worth taking: a chip that lasts 40 ms at 1× lasts 80 ms at 0.5×, which is
   * the difference between a tick and a shape with notes in it. Pitch-preserving
   * playback would need an overlap-add stretcher and would smear exactly the
   * transients the learner slowed the clip down to hear.
   */
  rate?: number;
  /** Repeat until stopped, rather than ending with the clip. */
  loop?: boolean;
}

export interface Playback {
  stop: () => void;
  /**
   * Elapsed 0..1 progress, polled by the waveform renderer.
   *
   * A looping clip wraps to 0 rather than arriving at 1, which is what keeps a
   * caller's animation loop — and the playhead it is drawing — alive across
   * repeats instead of tearing down at the end of the first one.
   */
  progress: () => number;
  /** Change speed without a gap, mid-clip. */
  setRate: (rate: number) => void;
  /** Turn repeat on or off, mid-clip. */
  setLoop: (on: boolean) => void;
}

/** A playback that never was — reported finished, so callers tear straight down. */
const INERT: Playback = {
  stop: () => {},
  progress: () => 1,
  setRate: () => {},
  setLoop: () => {},
};

/** Whatever is sounding right now, wherever in the app it was started from. */
let current: Playback | null = null;
let playSeq = 0;

/**
 * Stop whatever is playing.
 *
 * One sound at a time is a property of the app, not of any one screen: a
 * species page builds a player per recording and the quiz builds its own, and
 * only something above both of them can know that starting one means ending
 * another.
 */
export function stopAll(): void {
  const playing = current;
  current = null;
  playing?.stop();
}

export async function play(asset: AudioAsset, opts: PlayOptions = {}): Promise<Playback> {
  const mine = ++playSeq;
  stopAll();
  const ac = await ensureRunning();
  const { buffer } = await getBuffer(asset);
  // A later tap landed while this clip was being fetched, and it has already
  // started sounding. Starting now would layer this one under a recording the
  // learner has moved on from, so this call bows out.
  if (mine !== playSeq) return INERT;
  const src = ac.createBufferSource();
  src.buffer = buffer;
  src.connect(ac.destination);

  let rate = opts.rate ?? 1;
  let loop = opts.loop ?? false;
  src.playbackRate.value = rate;
  src.loop = loop;

  /**
   * Where the needle is, in clip seconds.
   *
   * Progress used to be context time since the start over the clip's length,
   * which both knobs break: at half speed a second of context time is half a
   * second of clip, and a loop sends the needle back to the beginning without
   * the context clock noticing. So the needle is tracked instead — `played` is
   * where it stood when a knob last moved, `markedAt` the context time that
   * happened — and moving a knob marks the position first, so the change
   * applies from here rather than rewriting the time already elapsed.
   */
  let played = 0;
  let markedAt = ac.currentTime;
  const position = () => {
    const at = played + (ac.currentTime - markedAt) * rate;
    return loop ? at % buffer.duration : at;
  };
  const mark = () => {
    // Read under the CURRENT loop flag, which is what makes switching repeat
    // off mid-clip land on the pass being played rather than on the sum of
    // every pass so far — a total that would read as "finished" at once.
    played = position();
    markedAt = ac.currentTime;
  };

  src.start();
  let stopped = false;
  src.onended = () => (stopped = true);
  const playback: Playback = {
    stop: () => {
      if (current === playback) current = null;
      if (!stopped) {
        try {
          src.stop();
        } catch {
          /* already stopped */
        }
      }
    },
    progress: () => {
      if (stopped) return 1;
      // The context stopped running and took the sound with it — iOS does this
      // when the app backgrounds mid-clip. Report the clip as over so the
      // caller resets, rather than animating a playhead that will never move
      // again and a strip that can never be tapped back to life.
      if (ac.state !== 'running') return 1;
      const at = position() / buffer.duration;
      return loop ? at : Math.min(1, at);
    },
    setRate: (next) => {
      mark();
      rate = next;
      src.playbackRate.value = next;
    },
    setLoop: (on) => {
      mark();
      loop = on;
      src.loop = on;
    },
  };
  current = playback;
  return playback;
}
